import React from 'react';

interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  rounded?: boolean;
  shimmer?: boolean;
  variant?: 'text' | 'rectangular' | 'circular';
}

export function Skeleton({ 
  className = '', 
  width, 
  height, 
  rounded = false, 
  shimmer = true,
  variant = 'rectangular'
}: SkeletonProps) {
  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  const variantClasses = {
    text: 'h-4 rounded',
    rectangular: rounded ? 'rounded-lg' : 'rounded',
    circular: 'rounded-full aspect-square'
  };

  return (
    <div
      className={`
        animate-pulse bg-white/10 
        ${shimmer ? 'shimmer' : ''}
        ${variantClasses[variant]} 
        ${className}
      `.trim()}
      style={style}
      role="presentation"
      aria-label="Loading content"
    />
  );
}

export function SongCardSkeleton() {
  return (
    <div className="bg-white/5 rounded-lg p-4 space-y-3 animate-fade-in">
      <div className="flex items-center space-x-3">
        <Skeleton width={48} height={48} variant="circular" />
        <div className="flex-1 space-y-2">
          <Skeleton height={16} width="60%" variant="text" />
          <div className="flex items-center space-x-2">
            <Skeleton height={14} width="30%" variant="text" />
            <div className="w-1 h-1 bg-white/20 rounded-full"></div>
            <Skeleton height={14} width="25%" variant="text" />
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Skeleton width={24} height={24} variant="circular" />
          <Skeleton width={24} height={24} variant="circular" />
          <Skeleton width={24} height={24} variant="circular" />
        </div>
      </div>
    </div>
  );
}

export function AlbumCardSkeleton() {
  return (
    <div className="bg-white/5 rounded-lg p-4 space-y-3 animate-fade-in group">
      <div className="relative aspect-square w-full">
        <Skeleton height="100%" className="w-full absolute inset-0" />
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Skeleton width={32} height={32} variant="circular" />
        </div>
      </div>
      <div className="space-y-2">
        <Skeleton height={18} width="80%" variant="text" />
        <Skeleton height={14} width="60%" variant="text" />
        <div className="flex items-center justify-between">
          <Skeleton height={12} width="40%" variant="text" />
          <Skeleton height={12} width="20%" variant="text" />
        </div>
      </div>
    </div>
  );
}

export function ArtistCardSkeleton() {
  return (
    <div className="bg-white/5 rounded-lg p-6 space-y-4 text-center animate-fade-in">
      <div className="flex justify-center">
        <Skeleton width={80} height={80} variant="circular" />
      </div>
      <div className="space-y-2">
        <Skeleton height={18} width="70%" className="mx-auto" variant="text" />
        <Skeleton height={14} width="50%" className="mx-auto" variant="text" />
        <div className="flex justify-center space-x-4 mt-3">
          <Skeleton height={12} width="30px" variant="text" />
          <Skeleton height={12} width="40px" variant="text" />
        </div>
      </div>
    </div>
  );
}

export function PlaylistSkeleton() {
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start space-x-6">
        <div className="flex-shrink-0">
          <Skeleton width={200} height={200} />
        </div>
        <div className="flex-1 space-y-4 pt-4">
          <div className="space-y-2">
            <Skeleton height={12} width="60px" variant="text" />
            <Skeleton height={36} width="60%" variant="text" />
          </div>
          <div className="space-y-2">
            <Skeleton height={16} width="40%" variant="text" />
            <Skeleton height={14} width="30%" variant="text" />
          </div>
          <div className="flex items-center space-x-3 pt-4">
            <Skeleton width={120} height={44} />
            <Skeleton width={32} height={32} variant="circular" />
            <Skeleton width={32} height={32} variant="circular" />
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 pt-4">
        <div className="grid grid-cols-12 gap-4 px-4 py-2 text-sm">
          <Skeleton height={12} width="20px" className="col-span-1" variant="text" />
          <Skeleton height={12} width="60%" className="col-span-6" variant="text" />
          <Skeleton height={12} width="80%" className="col-span-3" variant="text" />
          <Skeleton height={12} width="40px" className="col-span-2" variant="text" />
        </div>
      </div>
      <div className="space-y-1">
        {Array.from({ length: 8 }).map((_, i) => (
          <PlaylistSongSkeleton key={i} index={i + 1} />
        ))}
      </div>
    </div>
  );
}

export function PlaylistSongSkeleton({ index }: { index: number }) {
  return (
    <div className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-white/5 rounded group">
      <div className="col-span-1 flex items-center">
        <Skeleton height={14} width="20px" variant="text" />
      </div>
      <div className="col-span-6 flex items-center space-x-3">
        <Skeleton width={40} height={40} />
        <div className="space-y-1">
          <Skeleton height={16} width="70%" variant="text" />
          <Skeleton height={14} width="50%" variant="text" />
        </div>
      </div>
      <div className="col-span-3 flex items-center">
        <Skeleton height={14} width="60%" variant="text" />
      </div>
      <div className="col-span-2 flex items-center justify-end">
        <Skeleton height={14} width="30px" variant="text" />
      </div>
    </div>
  );
}

export function SearchResultsSkeleton() {
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex gap-2 flex-wrap">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} width={80} height={36} />
        ))}
      </div>
      <div className="space-y-8">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Skeleton height={24} width="120px" variant="text" />
            <Skeleton height={16} width="60px" variant="text" />
          </div>
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <SongCardSkeleton key={i} />
            ))}
          </div>
        </div>
        <div className="space-y-4">
          <Skeleton height={24} width="100px" variant="text" />
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <ArtistCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function PlayerControlsSkeleton() {
  return (
    <div className="bg-surface-dark/95 backdrop-blur-sm border-t border-surface-border p-4 animate-fade-in">
      <div className="flex items-center justify-between max-w-screen-xl mx-auto">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <Skeleton width={56} height={56} />
          <div className="space-y-1 min-w-0">
            <Skeleton height={16} width="150px" variant="text" />
            <Skeleton height={14} width="100px" variant="text" />
          </div>
          <Skeleton width={24} height={24} variant="circular" />
        </div>
        <div className="flex flex-col items-center space-y-2 flex-1">
          <div className="flex items-center space-x-4">
            <Skeleton width={28} height={28} variant="circular" />
            <Skeleton width={32} height={32} variant="circular" />
            <Skeleton width={40} height={40} variant="circular" />
            <Skeleton width={32} height={32} variant="circular" />
            <Skeleton width={28} height={28} variant="circular" />
          </div>
          <div className="flex items-center space-x-2 w-full max-w-md">
            <Skeleton height={12} width="30px" variant="text" />
            <Skeleton height={4} className="flex-1" />
            <Skeleton height={12} width="30px" variant="text" />
          </div>
        </div>
        <div className="flex items-center space-x-4 flex-1 justify-end">
          <Skeleton width={24} height={24} variant="circular" />
          <Skeleton width={24} height={24} variant="circular" />
          <div className="flex items-center space-x-2">
            <Skeleton width={20} height={20} variant="circular" />
            <Skeleton width={80} height={4} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function LibrarySkeleton() {
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton height={32} width="150px" variant="text" />
          <Skeleton width={100} height={36} />
        </div>
        <div className="flex gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} width={100} height={40} />
          ))}
        </div>
      </div>
      <div className="grid gap-4">
        {Array.from({ length: 12 }).map((_, i) => (
          <SongCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

export function PageSkeleton() {
  return (
    <div className="space-y-8 animate-fade-in">
      <div className="space-y-4">
        <Skeleton height={40} width="300px" variant="text" />
        <Skeleton height={20} width="500px" variant="text" />
      </div>
      <div className="space-y-12">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="space-y-6">
            <div className="flex items-center justify-between">
              <Skeleton height={28} width="200px" variant="text" />
              <Skeleton width={80} height={32} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {Array.from({ length: 4 }).map((_, j) => (
                <AlbumCardSkeleton key={j} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 